/**
 * Słownik jednostek miary używany w dialogach faktur.
 *
 * Z KSeF XML (pole `P_8A`) mogą przychodzić bardzo różne formy zapisu:
 * `L`, `l.`, `szt`, `sztuka`, `h`, `godz`, `kg`, `kilogram`, `m2`, itd.
 * MUI Select z ustaloną listą `<MenuItem>` "połyka" wartość, jeśli nie jest
 * identyczna ze znanym MenuItem — wtedy pole wygląda na puste.
 *
 * `normalizeUnit` mapuje wariant z KSeF na naszą kanoniczną wartość
 * (`szt.`, `kpl.`, `m`, `m²`, `m³`, `kg`, `t`, `l`, `godz.`, `usł.`),
 * a `ensureUnitOptions` pozwala wyświetlić niestandardową wartość
 * jako dodatkowy MenuItem (żeby nigdy niczego nie zgubić).
 */

export const DEFAULT_UNITS: string[] = [
  'szt.',
  'kpl.',
  'm',
  'm²',
  'm³',
  'kg',
  't',
  'l',
  'godz.',
  'usł.',
];

const UNIT_ALIASES: Record<string, string> = {
  '': 'szt.',
  SZT: 'szt.',
  'SZT.': 'szt.',
  SZTUKA: 'szt.',
  PC: 'szt.',
  PCS: 'szt.',
  KPL: 'kpl.',
  'KPL.': 'kpl.',
  KOMPL: 'kpl.',
  KOMPLET: 'kpl.',
  M: 'm',
  METR: 'm',
  MB: 'm',
  'MB.': 'm',
  M2: 'm²',
  'M^2': 'm²',
  'M²': 'm²',
  MKW: 'm²',
  M3: 'm³',
  'M^3': 'm³',
  'M³': 'm³',
  MSZ: 'm³',
  KG: 'kg',
  KILOGRAM: 'kg',
  KILO: 'kg',
  T: 't',
  TONA: 't',
  TON: 't',
  L: 'l',
  LTR: 'l',
  LITR: 'l',
  LITRY: 'l',
  DM3: 'l',
  H: 'godz.',
  GODZ: 'godz.',
  'GODZ.': 'godz.',
  HRS: 'godz.',
  HOUR: 'godz.',
  ROBOCZOGODZINA: 'godz.',
  RBH: 'godz.',
  USL: 'usł.',
  'USŁ': 'usł.',
  'USŁ.': 'usł.',
  USLUGA: 'usł.',
  'USŁUGA': 'usł.',
};

/** Zwraca kanoniczną jednostkę lub — jeśli wariant nieznany — oryginalny trim. */
export function normalizeUnit(raw?: string | number | null): string {
  if (raw === undefined || raw === null) return 'szt.';
  const s = String(raw).trim();
  if (!s) return 'szt.';
  const key = s.toUpperCase();
  if (UNIT_ALIASES[key]) return UNIT_ALIASES[key];
  const keyNoDot = key.replace(/\.$/, '');
  if (UNIT_ALIASES[keyNoDot]) return UNIT_ALIASES[keyNoDot];
  return s;
}

/**
 * Zwraca listę opcji do MUI Select z domyślnymi jednostkami + ewentualną
 * niestandardową wartością (np. "dag") dodaną na początku, tak aby
 * Select nigdy nie pokazywał pustego pola dla danych z KSeF.
 */
export function ensureUnitOptions(current?: string | null): string[] {
  const curr = (current || '').trim();
  if (!curr) return DEFAULT_UNITS;
  if (DEFAULT_UNITS.includes(curr)) return DEFAULT_UNITS;
  return [curr, ...DEFAULT_UNITS];
}
